import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { INFINITE_PAGE_SIZE_MODAL } from '../../../../../constants/templates';
import useAuthorizedFetch from '../../../../../hooks/useAuthorizedFetch';
import { TemplatesService } from '../../../../../service/TemplatesService';
import { selectAdminType, selectOrganizationInfo, selectUserDepartmentId } from '../../../../../store/selectors/auth';
import {
  ClaimLinkTemplateStatusEnum,
  IClaimLinkTemplatesPage,
  IClaimLinkTemplatesPageRequest,
} from '../../../../../types/templates';
import { filterClaimLinkTemplatesForUser } from '../../../../../utils/templates';

export const CLAIM_LINK_TEMPLATES_QUERY_SCOPE = 'claim-link-templates';

export interface IUseClaimLinkTemplatesInfiniteQueryOptions {
  searchQuery?: string;
  selectedDepartments?: string[];
  departmentId?: string;
  orgId?: string;
  enabled?: boolean;
}

const getNextPageParam = (lastPage: IClaimLinkTemplatesPage, allPages: IClaimLinkTemplatesPage[]) => {
  const loaded = allPages.reduce((total, page) => total + page.templates.length, 0);

  if (!lastPage.templates.length || loaded >= lastPage.total_count) {
    return undefined;
  }

  return allPages.length + 1;
};

export const useClaimLinkTemplatesInfiniteQuery = ({
  searchQuery,
  selectedDepartments,
  departmentId,
  orgId,
  enabled = true,
}: IUseClaimLinkTemplatesInfiniteQueryOptions) => {
  const runAuthorized = useAuthorizedFetch();
  const organizationInfo = useSelector(selectOrganizationInfo);
  const userDepartmentId = useSelector(selectUserDepartmentId);
  const adminType = useSelector(selectAdminType);

  const params = useMemo<Omit<IClaimLinkTemplatesPageRequest, 'page'>>(() => {
    const preparedQuery = searchQuery?.toLowerCase().trim();
    const departmentIds = departmentId ? [departmentId] : selectedDepartments ?? [];

    return {
      org_id: orgId || organizationInfo?.uid || '',
      page_size: INFINITE_PAGE_SIZE_MODAL,
      status: ClaimLinkTemplateStatusEnum.Live,
      ...(preparedQuery ? { search_query: preparedQuery } : {}),
      ...(departmentIds.length ? { department_ids: departmentIds } : {}),
    };
  }, [searchQuery, selectedDepartments, departmentId, orgId, organizationInfo?.uid]);

  const query = useInfiniteQuery({
    enabled,
    queryKey: [CLAIM_LINK_TEMPLATES_QUERY_SCOPE, params],
    queryFn: ({ pageParam, signal }) =>
      runAuthorized((token) => TemplatesService.getClaimLinkTemplates({ ...params, page: pageParam }, token), {
        signal,
      }),
    getNextPageParam,
    initialPageParam: 1,
    gcTime: 0,
  });

  const { data } = query;

  const templates = useMemo(
    () =>
      filterClaimLinkTemplatesForUser(data?.pages.flatMap((page) => page.templates) ?? [], userDepartmentId, adminType),
    [data, userDepartmentId, adminType],
  );

  const totalCount = data?.pages[0]?.total_count ?? 0;

  return {
    ...query,
    templates,
    totalCount,
  };
};
